// ApprovalList (Domain/Inbox; was HomeViews.tsx's, HIERARCHY follow-up 8, BL-189): what waits for the
// person's OK (approvals.list), one request a row on hairlines: who asked (an agent or the person's own AI
// tool), what it would do, when, and why; at the end, whether it waits for them or for someone else. Five
// at most (four in a chat), then how many more, then one way in: Decide them. In a chat an approval is
// never decided (DEC-009: a tap in a card is an ordinary tool call), only opened in Flowology. Reads only
// --approvals-*; Label, Button and Link read their own.
import type { ApprovalListProps } from '../../schemas.ts';
import type { ViewProps } from '../../host.ts';
import { plural } from '../../format.ts';
import { Label } from '../Label/Label.tsx';
import { agentName, agentTime, ViewAct } from '../view-act.tsx';
import './ApprovalList.css';

/** approvals.list */
export function ApprovalList({ data, host }: ViewProps<ApprovalListProps>) {
  const chat = host.surface === 'chat';
  const waiting = data.approvals.filter((a) => a.status === 'pending');
  const shown = waiting.slice(0, chat ? 4 : 5);
  if (!waiting.length)
    return (
      <p className="fl-approvals__empty" role="status">
        Nothing waits for your OK.
      </p>
    );
  // yours are those you may decide; the rest wait on someone else (QA-860: the owner's, for money)
  const yours = waiting.filter((a) => !a.cannotDecide).length;
  const others = waiting.length - yours;
  return (
    <article className="fl-approvals" data-surface={host.surface} aria-label={`${yours} waiting for your OK`}>
      <h3 className="fl-approvals__title">
        {yours ? `${plural(yours, 'thing waits', 'things wait')} for your OK` : 'Nothing waits for your OK'}
      </h3>
      {others > 0 && (
        <p className="fl-approvals__sub">
          {plural(others, 'request waits', 'requests wait')} for someone else’s OK.
        </p>
      )}
      <ul className="fl-approvals__rows">
        {shown.map((a) => {
          const what = Object.values(a.labels ?? {}).join(', ');
          return (
            <li key={a.id} className="fl-approvals__row">
              <span>
                <Label kind="muted">{a.agent.startsWith('user:') ? 'AI tool' : 'Agent'}</Label>{' '}
                <b>
                  {a.actionTitle}
                  {what ? `: ${what}` : ''}
                </b>
                <span className="fl-approvals__sub">
                  {agentName(a.agent)}, {agentTime(a.createdAt)}
                  {/* the time ends in a.m. or p.m., its own full stop */}
                  {a.explanation ? ` ${a.explanation}` : ''}
                </span>
                {a.cannotDecide && <span className="fl-approvals__sub">{a.cannotDecide}</span>}
              </span>
              {!chat &&
                (a.cannotDecide ? (
                  <Label kind="muted">Someone else decides</Label>
                ) : (
                  <Label kind="wait">Waiting for you</Label>
                ))}
            </li>
          );
        })}
      </ul>
      {waiting.length > shown.length && (
        <p className="fl-approvals__sub">And {waiting.length - shown.length} more.</p>
      )}
      <div className="fl-approvals__acts">
        <ViewAct surface={host.surface} primary onPress={() => host.open({ kind: 'approvals' })}>
          {chat ? 'Open in Flowology to approve' : 'Decide them'}
        </ViewAct>
      </div>
    </article>
  );
}
